import React, { useState, useEffect, useContext, useRef, Fragment } from "react";
import PropTypes from "prop-types";
import { Button, Select, DatePicker, TimePicker, Checkbox, message, Row, Col } from "antd";
import BaseModal from "../BaseModal";
import { fetchLocations } from "../../api/LocationsApi";
import AppConfig from "../../config/AppConfig";
import { WarehouseContext } from "../../context/WarehouseContext";
import { UserContext } from "../../context/UserContext";
import { FsuUpdateStatusApi } from "../../api/FsuUpdateStatusApi";
import { LineHaulApi } from "../../api/LineHaulApi";
import PickupCompletionModal from "./PickupCompletionModal";
import I18n from "../../common/I18n";
import moment from "moment";

const STATUS_OPTIONS = [
  { value: "ON_HAND", label: "On Hand" },
  { value: "RECEIVED", label: "Received" },
];

// Typing in the manifest dropdown is matched server-side, so keystrokes are coalesced
// before they become a request.
const MANIFEST_SEARCH_DEBOUNCE_MS = 300;

const FAILURE_REASON_LABELS = {
  order_not_found: "order not found",
  not_at_current_warehouse: "not at current warehouse",
  origin_warehouse_already_completed: "origin warehouse already completed",
  update_failed: "update failed",
};

// LTL orgs' "Update Status" bulk action (renders in the same footer slot as Switch Details -
// see OrderBulkOperationsContainerComponent). Collects status/location/datetime and applies the
// update directly - it does not gate on or ask to complete any pickup leg.
function FsuUpdateStatus(props) {
  const {
    selectedOrders,
    isFromBulkContainer,
    isFsuUpdateClicked,
    setIsFsuUpdateClicked,
    setIsHovered,
    onClose,
  } = props;

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrdersState, setSelectedOrdersState] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [currentWarehouseId, setCurrentWarehouseId] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDatetime, setSelectedDatetime] = useState(moment());
  const [showPickupCompletion, setShowPickupCompletion] = useState(false);
  const [pickupCandidates, setPickupCandidates] = useState([]);
  const [inProgress, setInProgress] = useState(false);
  const [enterManifestNumber, setEnterManifestNumber] = useState(false);
  const [manifestNumber, setManifestNumber] = useState(undefined);
  const [manifestOptions, setManifestOptions] = useState([]);
  const [manifestsLoading, setManifestsLoading] = useState(false);
  const [manifestSearch, setManifestSearch] = useState("");

  const { currentOrg } = useContext(UserContext);
  const { getWHDetails } = useContext(WarehouseContext);

  useEffect(() => {
    if (isFsuUpdateClicked) {
      setSelectedOrdersState(selectedOrders);
      setModalVisible(true);
    }
  }, [isFsuUpdateClicked]);

  useEffect(() => {
    if (isFromBulkContainer && !modalVisible) {
      setIsFsuUpdateClicked(false);
      setIsHovered(null);
    }
  }, [modalVisible]);

  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const warehouseResult = await fetchLocations(AppConfig.warehouseCode, 1, null);
        if (warehouseResult.success) {
          setWarehouses(warehouseResult.locations.locations);
        } else {
          message.error(`Error fetching ${I18n.t("general.warehouse_small")} locations`);
        }
      } catch (error) {
        message.error(`Error fetching ${I18n.t("general.warehouse_small")} locations`);
      }
    };
    fetchWarehouseData();
  }, []);

  useEffect(() => {
    const fetchCurrentWarehouse = async () => {
      try {
        const currentWarehouseDetails = getWHDetails();
        if (currentWarehouseDetails?.id) {
          setCurrentWarehouseId(currentWarehouseDetails.id);
          setSelectedWarehouse(currentWarehouseDetails.id);
        }
      } catch (error) {
        message.error(`Error fetching current ${I18n.t("general.warehouse_small")}`);
      }
    };
    fetchCurrentWarehouse();
  }, []);

  // BulkStatusUpdateService batches the Warehouse Transfer call by each order's pre-update
  // wh_location_id, so that - not the popup's current warehouse - is the lane a manifest would
  // be filed against. A blank wh_location_id is kept as its own entry rather than dropped, so a
  // selection that is only partly resolvable still reads as mixed instead of as one lane.
  const originWarehouseIds = [...new Set(selectedOrdersState.map((order) => String(order.wh_location_id || "")))];
  const laneOriginId = originWarehouseIds.length === 1 && originWarehouseIds[0] !== "" ? originWarehouseIds[0] : null;
  // Orders received back at their own origin never left, so there is no transfer to file and no
  // lane to list - the API answers that lane empty anyway.
  const isLaneResolved = Boolean(laneOriginId) && Boolean(selectedWarehouse) && laneOriginId !== selectedWarehouse;

  // Typing can land responses out of order; only the newest request may write state.
  const manifestRequestSeq = useRef(0);

  const loadManifests = async (searchTerm) => {
    const requestId = manifestRequestSeq.current + 1;
    manifestRequestSeq.current = requestId;
    setManifestsLoading(true);
    try {
      const response = await LineHaulApi.fetchTransferCandidateManifests(
        currentOrg.id,
        laneOriginId,
        selectedWarehouse,
        searchTerm
      );
      if (requestId !== manifestRequestSeq.current) return;

      if (!response.success) {
        setManifestOptions([]);
        message.error(response.message || "Error fetching manifests", 5);
        return;
      }
      setManifestOptions(response.manifests);
    } catch (error) {
      if (requestId !== manifestRequestSeq.current) return;
      setManifestOptions([]);
      message.error("Error fetching manifests", 5);
    } finally {
      if (requestId === manifestRequestSeq.current) {
        setManifestsLoading(false);
      }
    }
  };

  // A changed lane invalidates both the list and anything already picked from it: a manifest
  // valid for one lane is not valid for another, and the transfer would refuse it.
  useEffect(() => {
    setManifestNumber(undefined);
    setManifestOptions([]);
    setManifestSearch("");
  }, [laneOriginId, selectedWarehouse]);

  // The checkbox is hidden when the lane can't be resolved, so it must not stay ticked from an
  // earlier selection - a stale tick would block Update behind a field that isn't rendered.
  useEffect(() => {
    if (!isLaneResolved) {
      setEnterManifestNumber(false);
    }
  }, [isLaneResolved]);

  useEffect(() => {
    if (!modalVisible || !enterManifestNumber || !isLaneResolved) return undefined;

    // Searching hits the server and is debounced; first opening the dropdown should not be.
    const timer = setTimeout(() => loadManifests(manifestSearch),
      manifestSearch ? MANIFEST_SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [modalVisible, enterManifestNumber, isLaneResolved, laneOriginId, selectedWarehouse, manifestSearch]);

  const resetForm = () => {
    setSelectedStatus("");
    setSelectedDatetime(moment());
    setShowPickupCompletion(false);
    setPickupCandidates([]);
    setEnterManifestNumber(false);
    setManifestNumber(undefined);
    setManifestOptions([]);
    setManifestSearch("");
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    resetForm();
  };

  const finishAndClose = () => {
    setModalVisible(false);
    resetForm();
    if (onClose && typeof onClose === "function") {
      onClose();
    }
    props.resetSelection();
    props.refreshOrders();
  };

  const submitUpdate = async (pickupCompletions = []) => {
    setInProgress(true);
    try {
      const response = await FsuUpdateStatusApi.bulkStatusUpdate(
        currentOrg.id,
        currentWarehouseId,
        selectedWarehouse,
        selectedStatus,
        selectedDatetime.toISOString(),
        selectedOrdersState.map((order) => order.id),
        pickupCompletions,
        enterManifestNumber ? manifestNumber || null : null
      );

      if (response.success) {
        const summary = response.summary || {};
        if (summary.failed > 0) {
          const reasons = [...new Set((response.results || [])
            .filter((result) => result.status === "failed")
            .map((result) => FAILURE_REASON_LABELS[result.reason] || result.reason))];
          const reasonText = reasons.length > 0 ? ` (${reasons.join("; ")})` : "";
          message.warning(`${summary.succeeded} of ${summary.total} orders updated. ${summary.failed} failed${reasonText}.`, 8);
        } else {
          message.success("Successfully updated order status");
        }
        finishAndClose();
        return;
      }

      if (response.errors) {
        response.errors.forEach((error) => message.error(error, 5));
      } else {
        message.error("Error updating status");
      }
    } catch (error) {
      message.error("Error updating status");
    } finally {
      setInProgress(false);
    }
  };

  // On Hand always implies any pending pickup legs need completing; Received implies the same
  // only when the selected warehouse isn't the order's origin (see BulkStatusUpdateService -
  // pickup_leg_completion_applicable?). Check which of the selected orders actually still need it
  // (also excludes orders already COMPLETED or already past their origin warehouse per Linehaul
  // tracking) before asking for driver/completion details. None left needing it -> submit
  // directly.
  const handleUpdateClick = async () => {
    if (selectedStatus !== "ON_HAND" && selectedStatus !== "RECEIVED") {
      submitUpdate([]);
      return;
    }

    setInProgress(true);
    try {
      const response = await FsuUpdateStatusApi.fetchPickupLegCandidates(
        currentOrg.id,
        selectedOrdersState.map((order) => order.id),
        selectedStatus,
        selectedWarehouse
      );
      if (!response.success) {
        (response.errors || ["Error checking pickup completion status"]).forEach((error) => message.error(error, 5));
        return;
      }

      const candidates = response.candidates || [];
      if (candidates.length === 0) {
        await submitUpdate([]);
        return;
      }
      setPickupCandidates(candidates);
      setShowPickupCompletion(true);
    } catch (error) {
      message.error("Error checking pickup completion status");
    } finally {
      setInProgress(false);
    }
  };

  const handlePickupCompletionBack = () => setShowPickupCompletion(false);
  const handlePickupCompletionComplete = (pickupCompletions) => submitUpdate(pickupCompletions);

  const isUpdateEnabled = () => (
    !inProgress && selectedStatus && selectedWarehouse && selectedDatetime && selectedOrdersState.length > 0
    && (!enterManifestNumber || Boolean(manifestNumber))
  );

  const Footer = () => (
    <Row id="fsu-footer-row" type="flex" justify="space-between" align="middle" style={{ width: "100%" }}>
      <Col id="fsu-footer-cancel-col">
        <Button id="fsu-cancel-button" data-testid="fsu-cancel-button" onClick={handleCloseModal} disabled={inProgress}>
          Cancel
        </Button>
      </Col>
      <Col id="fsu-footer-update-col">
        <Button
          id="fsu-update-button"
          data-testid="fsu-update-button"
          type="primary"
          onClick={handleUpdateClick}
          disabled={!isUpdateEnabled()}
          loading={inProgress}
        >
          Update
        </Button>
      </Col>
    </Row>
  );

  // Matches the Figma "Update Status" modal (node 7088:17381): a two-line header ("Update
  // Status" + "N orders selected"), then Order Status / At Location stacked full-width, then
  // Date and Time as two separate fields side by side.
  const handleDateChange = (value) => {
    if (!value) return;
    setSelectedDatetime((prev) => (prev || moment()).clone()
      .year(value.year()).month(value.month()).date(value.date()));
  };

  const handleTimeChange = (value) => {
    if (!value) return;
    setSelectedDatetime((prev) => (prev || moment()).clone()
      .hour(value.hour()).minute(value.minute()).second(0));
  };

  const ModalTitle = () => (
    <div id="fsu-modal-title" data-testid="fsu-modal-title">
      <div id="fsu-modal-heading" className="fsu-modal-heading">Update Status</div>
      <div id="fsu-modal-subtitle" className="fsu-modal-subtitle">{`${selectedOrdersState.length} orders selected`}</div>
    </div>
  );

  const renderUpdateStatusModal = () => (
    <BaseModal
      id="fsu-update-status-base-modal"
      title={<ModalTitle />}
      visible={modalVisible}
      onCancel={handleCloseModal}
      destroyOnClose
      width={360}
      maskClosable={false}
      footer={<Footer />}
    >
      <div id="fsu-update-status-modal" data-testid="fsu-update-status-modal">
        <Row id="fsu-status-row" style={{ marginBottom: 12 }}>
          <Col id="fsu-status-col" span={24}>
            <div id="fsu-status-label" className="fsu-label">Order Status</div>
            <Select
              id="fsu-status-input"
              data-testid="fsu-status-input"
              placeholder="Select Status"
              value={selectedStatus || undefined}
              style={{ width: "100%" }}
              onChange={(value) => setSelectedStatus(value)}
              disabled={inProgress}
            >
              {STATUS_OPTIONS.map((option) => (
                <Select.Option id={`fsu-status-option-${option.value}`} key={option.value} value={option.value}>{option.label}</Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row id="fsu-location-row" style={{ marginBottom: 12 }}>
          <Col id="fsu-location-col" span={24}>
            <div id="fsu-location-label" className="fsu-label">At Location</div>
            <Select
              id="fsu-location-input"
              data-testid="fsu-location-input"
              placeholder={`Select ${I18n.t("general.warehouse")}`}
              value={selectedWarehouse || undefined}
              style={{ width: "100%" }}
              onChange={(value) => setSelectedWarehouse(value)}
              disabled={inProgress}
              showSearch
              filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {warehouses.map((warehouse) => (
                <Select.Option id={`fsu-location-option-${warehouse.id}`} key={warehouse.id} value={warehouse.id}>
                  {warehouse.location_code || warehouse.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row id="fsu-datetime-row" gutter={12}>
          <Col id="fsu-date-col" span={12}>
            <div id="fsu-date-label" className="fsu-label">Date</div>
            <DatePicker
              id="fsu-date-input"
              data-testid="fsu-date-input"
              value={selectedDatetime}
              onChange={handleDateChange}
              format="MM/DD/YYYY"
              style={{ width: "100%" }}
              disabled={inProgress}
            />
          </Col>
          <Col id="fsu-time-col" span={12}>
            <div id="fsu-time-label" className="fsu-label">Time</div>
            {/* antd v3's TimePicker doesn't forward unknown props (like id/data-testid) to its
                DOM - wrap it so the field still has stable id/test hooks. */}
            <div id="fsu-time-input" data-testid="fsu-time-input">
              <TimePicker
                value={selectedDatetime}
                onChange={handleTimeChange}
                use12Hours
                format="h:mm A"
                style={{ width: "100%" }}
                disabled={inProgress}
              />
            </div>
          </Col>
        </Row>
        {isLaneResolved && (
          <Row id="fsu-manifest-checkbox-row" style={{ marginTop: 12 }}>
            <Col id="fsu-manifest-checkbox-col" span={24}>
              <Checkbox
                id="fsu-enter-manifest-checkbox"
                data-testid="fsu-enter-manifest-checkbox"
                checked={enterManifestNumber}
                onChange={(e) => setEnterManifestNumber(e.target.checked)}
                disabled={inProgress}
              >
                I want to enter the manifest number
              </Checkbox>
            </Col>
          </Row>
        )}
        {originWarehouseIds.length > 1 && (
          <Row id="fsu-manifest-mixed-origin-row" style={{ marginTop: 12 }}>
            <Col id="fsu-manifest-mixed-origin-col" span={24}>
              <div id="fsu-manifest-mixed-origin-note" style={{ color: "#8c8c8c", fontSize: 12 }}>
                {`The selected orders start from more than one ${I18n.t("general.warehouse_small")}, `
                  + "so they travel different lanes - a manifest number can only be picked when every "
                  + "selected order starts from the same one."}
              </div>
            </Col>
          </Row>
        )}
        {isLaneResolved && enterManifestNumber && (
          <Row id="fsu-manifest-number-row" style={{ marginTop: 8 }}>
            <Col id="fsu-manifest-number-col" span={24}>
              <div id="fsu-manifest-number-label" className="fsu-label">Manifest Number</div>
              {/* Only the completed legs the Warehouse Transfer API would accept for this lane -
                  picked, never typed, so a typo can't become a 404 on the transfer after the
                  local status update has already committed. Search is matched server-side, so
                  the client-side filter is switched off. */}
              <Select
                id="fsu-manifest-number-input"
                data-testid="fsu-manifest-number-input"
                placeholder="Select Manifest Number"
                value={manifestNumber}
                style={{ width: "100%" }}
                onChange={(value) => setManifestNumber(value)}
                onSearch={(value) => setManifestSearch(value)}
                showSearch
                filterOption={false}
                loading={manifestsLoading}
                disabled={inProgress}
                notFoundContent={manifestsLoading ? "Loading..." : "No manifests found for this lane"}
              >
                {manifestOptions.map((manifest) => (
                  <Select.Option
                    id={`fsu-manifest-option-${manifest.id}`}
                    key={manifest.id}
                    value={manifest.route_manifest_number}
                  >
                    {manifest.route_manifest_number}
                  </Select.Option>
                ))}
              </Select>
              <div id="fsu-manifest-eligibility-note" style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>
                Only completed manifests are eligible.
              </div>
            </Col>
          </Row>
        )}
        {inProgress && (
          <Row id="fsu-in-progress-row" style={{ marginTop: 12 }}>
            <Col span={24}>
              <div id="fsu-in-progress-notice" style={{ color: "#8c8c8c", fontSize: 12 }}>
                Updating status{selectedOrdersState.length > 1 ? ` for ${selectedOrdersState.length} orders` : ""} -
                this can take a little while when orders span multiple warehouses. Please don't close this window.
              </div>
            </Col>
          </Row>
        )}
      </div>
    </BaseModal>
  );

  return (
    <Fragment>
      {!isFromBulkContainer ? (
        <Button id="fsu-open-modal-button" type="primary" onClick={() => setModalVisible(true)}>
          {I18n.t("order.fsu_update_status", { defaultValue: "Update Status" })}
        </Button>
      ) : (
        I18n.t("order.fsu_update_status", { defaultValue: "Update Status" })
      )}
      {modalVisible && !showPickupCompletion && renderUpdateStatusModal()}
      {modalVisible && showPickupCompletion && (
        <PickupCompletionModal
          visible
          candidates={pickupCandidates}
          totalOrders={selectedOrdersState.length}
          statusLabel={STATUS_OPTIONS.find((option) => option.value === selectedStatus)?.label || selectedStatus}
          locationLabel={warehouses.find((warehouse) => warehouse.id === selectedWarehouse)?.location_code || ""}
          defaultDatetime={selectedDatetime}
          inProgress={inProgress}
          onBack={handlePickupCompletionBack}
          onComplete={handlePickupCompletionComplete}
          onCancel={handleCloseModal}
        />
      )}
    </Fragment>
  );
}

FsuUpdateStatus.propTypes = {
  selectedOrders: PropTypes.array,
  isFromBulkContainer: PropTypes.bool,
  isFsuUpdateClicked: PropTypes.bool,
  setIsFsuUpdateClicked: PropTypes.func,
  setIsHovered: PropTypes.func,
  resetSelection: PropTypes.func,
  refreshOrders: PropTypes.func,
  onClose: PropTypes.func,
};

export default FsuUpdateStatus;
